import { useContext, useEffect, useState } from 'react';

import { Backdrop, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';

import { getEnvFromOrigin, AppContext } from '../contexts/AppContext';
import { App } from '../contexts/AppContext/types';
import { hasRegistered } from '../lib/jwt';
import Routes from '../lib/routes';
import { getSDKClient } from '../lib/sdk';

export default function CallbackV2(): JSX.Element {
  const [oauthErrorCode, setOauthErrorCode] = useState<string | undefined>(undefined);
  const { app, setApp } = useContext(AppContext);
  const history = useHistory();
  const query = new URLSearchParams(useLocation().search);
  const code = query.get('code');
  const state = query.get('state');
  const { nextRedirect } = app;

  useEffect(() => {
    const codeExchange = async (code: string, state: string) => {
      const env = getEnvFromOrigin();
      const client = getSDKClient(env, ''); // no JWT needed for code exchange

      try {
        const token = await client.codeExchange(code, state);
        setApp((prevState: App) => {
          const newTokens = prevState.tokens;
          newTokens.set(env, token.access_token);
          return {
            ...prevState,
            newTokens,
          };
        });

        // Check email verified
        if (token.oauth_status === 'EMAIL_NOT_VERIFIED') {
          return history.push(`${Routes.Error}/EMAIL_NOT_VERIFIED`);
        }

        // Otherwise show either homepage,
        // or hubspot page if not registered yet
        if (hasRegistered(token.access_token)) {
          const route = nextRedirect;
          // clear nextRedirect so later logins don’t reuse it
          setApp((prevState: App) => ({
            ...prevState,
            nextRedirect: Routes.Overview,
          }));
          history.push(route || Routes.Overview);
        } else {
          history.push(Routes.Register);
        }
      } catch (e) {
        if (axios.isAxiosError(e) && e.response !== undefined) {
          setOauthErrorCode(e.response.data.error.error_code);
        } else {
          setOauthErrorCode('INTERNAL_ERROR');
        }
      }
    };

    if (code !== null && state !== null) {
      codeExchange(code, state);
    }
  }, [code, history, nextRedirect, setApp, state]);

  if (oauthErrorCode === undefined) {
    return (
      <Backdrop open={true}>
        <CircularProgress
          size={64}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-12px',
            marginLeft: '-12px',
          }}
        />
      </Backdrop>
    );
  } else {
    return (
      <div className="oauth-error">
        <a href="https://www.finverse.com">
          <img width="200" src={process.env.PUBLIC_URL + '/logo.svg'} />
        </a>
        <h1>Authorization Error</h1>
        <h3>Failed to login. This can happen if you took too long to login. Please try again.</h3>
        (Error Code: {oauthErrorCode})
      </div>
    );
  }
}
